import axios from 'axios'
import { useEffect, useState } from 'react'
import Spinner from '../../components/spinner'
import { Track, useAudioPlayer } from '../../components/audioPlayer/provider'
import Utils from '../../tools/utils'
import { useNavigate, useParams } from 'react-router-dom'
import { DEFAULT_USER, useUser } from '../../queries/user'
import { Delete, MoreVert, OpenInNew, PlaylistAdd, Shield } from '@nine-thirty-five/material-symbols-react/sharp'
import { Pause, PlayArrow } from '@nine-thirty-five/material-symbols-react/sharp/filled'
import { Button, IconButton, Link, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@iziplay/ui-kit'
import { AlbumCover } from '../../components/albumCover'


// The service sends the couple only, so each client builds its own form of it
const appleAlbumUrl = (storefront: string, appleId: string) =>
  `https://music.apple.com/${storefront}/album/${appleId}`

export function MusicRelease() {
  const navigate = useNavigate()
  const params = useParams()
  const user = useUser().data ?? DEFAULT_USER

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [release, setRelease] = useState<any>()
  const [tracks, setTracks] = useState<Track[]>([])

  const { setPlaylistPlayId, currentTrack, pause, play, playId, playing, playlist, addToPlaylist } = useAudioPlayer()

  useEffect(() => {
    const ws = new WebSocket(`${(import.meta.env.REACT_APP_IZIPLAY_URL_MUSIC as string)}/ws/global`)
    ws.onmessage = (data) => {
      const msg = JSON.parse(data.data)
      if (msg.type === 'pending') {
        setTracks((trs) => trs.map(track => {
          if (track.id !== msg.value.trackId || track.status === 'COMPLETED') {
            return track
          }
          return {
            ...track,
            status: 'PENDING'
          }
        }))
      }
      if (msg.type === 'downloading') {
        setTracks((trs) => trs.map(track => {
          if (track.id !== msg.value.trackId || track.status === 'COMPLETED') {
            return track
          }
          return {
            ...track,
            status: `DOWNLOADING (${Math.round(msg.value.percent)}%)`
          }
        }))
      }
      if (msg.type === 'complete') {
        setTracks((trs) => trs.map(track => {
          if (track.id !== msg.value.trackId) {
            return track
          }
          return {
            ...track,
            status: 'COMPLETED'
          }
        }))
      }
      if (msg.type === 'failed') {
        // FAILED rather than UNKNOWN: a prepare with no usable peer reports itself this way (was an HTTP 401)
        setTracks((trs) => trs.map(track => {
          if (track.id !== msg.value.trackId || track.status === 'COMPLETED') {
            return track
          }
          return {
            ...track,
            status: 'FAILED'
          }
        }))
      }
    }
    return () => {
      ws.close()
    }
  }, [])

  const fetchRelease = async () => {
    const res = await axios.request({
      method: 'GET',
      url: `${(import.meta.env.REACT_APP_IZIPLAY_URL_MUSIC as string)}/artist/${params.artist}/release/${params.release}`
    })
    setRelease(res.data.release)

    const mTracks: Track[] = []
    for (const medium of res.data.release.media) {
      for (const track of medium.tracks.sort((a: any, b: any) => a.position - b.position)) {
        mTracks.push({
          id: track.id,
          name: track.name,
          artist: {
            id: res.data.release.artist.id,
            name: res.data.release.artist.name
          },
          release: {
            id: res.data.release.id,
            name: res.data.release.name
          },
          medium: medium.position,
          position: track.position,
          url: `${(import.meta.env.REACT_APP_IZIPLAY_URL_MUSIC as string)}/artist/${res.data.release.artist.id}/release/${res.data.release.id}/medium/${medium.id}/position/${track.position}/file`,
          status: track.status,
          duration: track.duration
        })
      }
    }
    setTracks(mTracks)
    setIsLoading(false)
  }

  useEffect(() => {
    fetchRelease()
  }, [])

  const deleteRelease = async () => {
    if (!window.confirm(`Reset all tracks of "${release.name}"? The release will need to be prepared again.`)) {
      return
    }
    await axios.request({
      method: 'DELETE',
      url: `${(import.meta.env.REACT_APP_IZIPLAY_URL_MUSIC as string)}/artist/${params.artist}/release/${params.release}`,
      headers: {
        Authorization: `Bearer ${window.localStorage.token}`
      }
    })
    await fetchRelease()
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <div className="scrollable" style={{ display: 'flex', flexDirection: 'column', fontSize: '1rem' }}>
      <div>
        <div style={{ display: 'flex', flexDirection: 'row', paddingBottom: '20px', marginLeft: '14px', marginRight: '14px' }}>
          <AlbumCover
            src={`${(import.meta.env.REACT_APP_IZIPLAY_URL_MUSIC as string)}/artist/${release?.artist!.id}/release/${release.id}/artwork?size=400`}
            animatedSrc={release.hasAnimatedCover ? `${(import.meta.env.REACT_APP_IZIPLAY_URL_MUSIC as string)}/artist/${release?.artist!.id}/release/${release.id}/artwork/animated` : undefined}
            animate={release.hasAnimatedCover}
            title={`Cover of ${release.name}`}
            style={{ width: 300, height: 300, borderRadius: '10px', flexShrink: 0 }}
          />
          <div style={{ display: 'flex', padding: '40px 20px 0px 20px', flexDirection: 'column', width: '100%' }}>
            <Typography variant="h2" style={{ fontWeight: 'bold' }}>{release.name}</Typography>
            <Link
              onClick={() => {
                navigate(`/music/${release?.artist?.id}`)
              }}
              style={{ cursor: 'pointer' }}
            >
              <Typography variant="h5" style={{ fontWeight: 400 }}>{release?.artist?.name}</Typography>
            </Link>
            {release.year && (
              <Typography variant="subtitle1" color="textSecondary">{release.year}</Typography>
            )}
            <div style={{ flexGrow: '1' }} />
            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayArrow size={24} />}
                onClick={() => {
                  setPlaylistPlayId(tracks, tracks[0].id)
                }}
              >
                Play
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PlaylistAdd size={24} />}
                onClick={() => {
                  addToPlaylist(tracks)
                }}
              >
                Add to playlist
              </Button>
              <div style={{ flexGrow: '1' }} />
              {user.role >= 1 && (
                <>
                  <Menu
                    trigger={
                      <IconButton color="primary" title="Admin actions">
                        <MoreVert size={24} />
                      </IconButton>
                    }
                  >
                    <MenuItem
                      href={`https://musicbrainz.org/release/${release.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <ListItemIcon><OpenInNew size={24} /></ListItemIcon>
                      <ListItemText>MusicBrainz</ListItemText>
                    </MenuItem>
                    {/* One per storefront: the same album can be present in one and missing from another */}
                    {(release.appleAlbums ?? []).map((album: any) => (
                      <MenuItem
                        key={`${album.storefront}/${album.appleId}`}
                        href={appleAlbumUrl(album.storefront, album.appleId)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ListItemIcon><OpenInNew size={24} /></ListItemIcon>
                        <ListItemText>Apple Music ({album.storefront.toUpperCase()})</ListItemText>
                      </MenuItem>
                    ))}
                    <MenuItem
                      to={`/music/prepare?type=metabrainz&artist=${encodeURIComponent(release?.artist?.id)}&release=${encodeURIComponent(release?.id)}`}
                    >
                      <ListItemIcon><Shield size={24} /></ListItemIcon>
                      <ListItemText>Prepare again</ListItemText>
                    </MenuItem>
                    <MenuItem onClick={deleteRelease}>
                      <ListItemIcon><Delete size={24} /></ListItemIcon>
                      <ListItemText>Delete release</ListItemText>
                    </MenuItem>
                  </Menu>
                </>
              )}
            </div>
          </div>
        </div>
        <List style={{ flex: 1, overflowY: 'auto' }}>
          {
            tracks.map(track => (
              <ListItem
                key={track.id}
                disablePadding
              >
                <ListItemButton
                  style={{ padding: '16px' }}
                  onClick={() => {
                    if (currentTrack?.id === track.id && playing) {
                      return pause()
                    }
                    if (currentTrack?.id === track.id && !playing) {
                      return play()
                    }

                    const index = playlist.findIndex(t => t.id === track.id)
                    if (index === -1) {
                      setPlaylistPlayId(tracks, track.id)
                      return
                    }

                    playId(track.id)
                  }}
                  disabled={track.status !== 'COMPLETED'}
                >
                  <ListItemText primary={`${release.media.length > 1 ? `CD${track.medium} ` : ''}${track.position}. ${track.name}`} secondary={track.status} />
                  <Typography style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }} variant="subtitle2" color="textSecondary">
                    {Utils.transformDurateMiliseconds(track.duration)}
                  </Typography>
                  <ListItemIcon>{currentTrack?.id === track.id && (playing ? <PlayArrow size={24} /> : <Pause size={24} />)}</ListItemIcon>
                </ListItemButton>
              </ListItem>
            ))
          }
        </List>
      </div>
    </div>
  )
}
